import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for Google OAuth authentication (Merchant App)
 * Handles Google Sign-In for merchant registration and login
 */
export const useGoogleOAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Handle Google OAuth login/registration
   * @param {string} idToken - Google ID token from OAuth response
   * @param {string} mode - 'signin' for existing users, 'signup' for new users
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  const handleGoogleAuth = useCallback(async (idToken, mode = 'signin') => {
    setLoading(true);
    setError(null);

    try {
      // Use relative URL - Vercel rewrites will proxy to Railway backend
      // mode: 'signin' for login page (existing users only)
      // mode: 'signup' for signup page (new users only)
      const endpoint = mode === 'signup'
        ? '/api/merchant/oauth/google/signup'
        : '/api/merchant/oauth/google/signin';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important: Include cookies for authentication
        body: JSON.stringify({ idToken })
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMessage = data.error || 'Google authentication failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      // Token is stored in HttpOnly cookie by backend - DO NOT store in localStorage
      // This prevents XSS attacks from stealing the token

      // Handle navigation based on onboarding status
      if (data.user?.needsOnboarding || !data.user?.onboardingCompleted) {
        // New user - redirect to onboarding
        navigate('/onboarding', {
          state: {
            newUser: true,
            email: data.user.email,
            fromOAuth: true
          }
        });
      } else {
        // Existing user - check for redirect parameter
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrl = urlParams.get('redirect');
        if (redirectUrl && redirectUrl.startsWith('/')) {
          window.location.href = decodeURIComponent(redirectUrl);
        } else {
          window.location.href = '/overview';
        }
      }

      return { success: true, data };
    } catch (err) {
      console.error('[Google OAuth] Error:', err);
      const errorMessage = 'Network error. Please check your connection and try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  /**
   * Link Google account to existing merchant account (authenticated)
   * @param {string} idToken - Google ID token from OAuth response
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  const linkGoogleAccount = useCallback(async (idToken) => {
    setLoading(true);
    setError(null);

    try {
      // Use relative URL - Vercel rewrites will proxy to Railway backend
      // Token is sent via HttpOnly cookie (credentials: 'include')
      const res = await fetch('/api/merchant/oauth/link/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ idToken })
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMessage = data.error || 'Failed to link Google account';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      return { success: true, data };
    } catch (err) {
      console.error('[Google OAuth Link] Error:', err);
      const errorMessage = 'Network error. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    handleGoogleAuth,
    linkGoogleAccount,
    loading,
    error,
    setError
  };
};

export default useGoogleOAuth;
